/// <reference path="../../../types/axios-fix.d.ts" />
import { BaseService, httpClientService, authInterceptorService, errorHandlerService, cacheService, ApiResponse } from '../core';

/**
 * Team interface
 */
export interface Team {
  _id: string;
  id?: string;
  name: string;
  description?: string;
  organizationId: string;
  organizationName?: string;
  subscriptionId?: string;
  membersCount?: number;
  memberCount?: number;
  contentCount?: number;
  storageUsedBytes?: number;
  createdAt?: Date;
  updatedAt?: Date;
}

/**
 * Team Member interface
 */
export interface TeamMember {
  id: string;
  user_id: string;
  team_id: string;
  role_id: string;
  role_name?: string;
  user?: {
    id: string;
    name: string;
    email: string;
    avatar?: string;
  };
  joined_at?: string;
  invited_by?: string;
}

/**
 * Team Role interface
 */
export interface TeamRole {
  id: string;
  name: string;
  description?: string;
  permissions?: string[];
  is_system?: boolean;
  created_at?: string;
  updated_at?: string;
}

/**
 * Team Invitation interface
 */
export interface TeamInvitation {
  id: string;
  team_id: string;
  team_name?: string;
  organization_id: string;
  organization_name?: string;
  invitee_email: string;
  inviter_id: string;
  inviter_name?: string;
  role_id: string;
  role_name?: string;
  status: 'pending' | 'accepted' | 'declined' | 'expired';
  personal_message?: string;
  expires_at?: string;
  created_at?: string;
  accepted_at?: string;
  declined_at?: string;
}

/**
 * Team Overview interface
 */
export interface TeamOverview {
  id: string;
  name: string;
  description: string;
  membersCount: number;
  recentActivity: Array<{
    id: string;
    type: string;
    description: string;
    timestamp: string;
  }>;
  statistics: {
    totalContent: number;
    activeMembers: number;
    storageUsed: number;
  };
}

/**
 * Team Subscription interface
 */
export interface TeamSubscription {
  organization_subscription: any;
  team_usage: {
    current_member_count: number;
    storage_used_mb: number;
    content_count: number;
  };
}

/**
 * Create Team Request
 */
export interface CreateTeamRequest {
  name: string;
  description?: string;
  organizationId: string;
  subscriptionId?: string;
}

/**
 * Update Team Request
 */
export interface UpdateTeamRequest {
  name?: string;
  description?: string;
}

/**
 * Add Team Member Request
 */
export interface AddTeamMemberRequest {
  user_id: string;
  role_id: string;
}

/**
 * Create Team Invitation Request
 */
export interface CreateTeamInvitationRequest {
  invitee_email: string;
  role: string;
  personal_message?: string;
}

/**
 * Team Service
 * 
 * Handles all team-related operations including:
 * - Team CRUD operations
 * - Team member management
 * - Team roles and permissions
 * - Team invitations
 * - Team statistics and activity
 * 
 * Extracted from the monolithic API service.
 */
export class TeamService extends BaseService {
  constructor() {
    super(httpClientService, authInterceptorService, errorHandlerService, cacheService);
  }

  // ===== TEAM CRUD OPERATIONS =====

  /**
   * Get team details by ID
   */
  async getTeam(teamId: string): Promise<Team> {
    try {
      console.log(`Fetching team: ${teamId}`);
      
      const response = await this.getCached<any>(
        `/teams/${teamId}`,
        undefined,
        { timeout: 10000 }
      );
      
      // Handle nested team data structure
      const teamData = response.team || response;
      
      return {
        _id: teamData.id || teamData._id,
        id: teamData.id || teamData._id,
        name: teamData.name,
        description: teamData.description,
        organizationId: teamData.organization_id || teamData.organizationId,
        // Check if organization data is included in the response
        organizationName: teamData.organizationName || teamData.organization_name ||
                         (teamData.organization && teamData.organization.name) || undefined,
        subscriptionId: teamData.subscriptionId || teamData.subscription_id || undefined,
        membersCount: teamData.members_count || teamData.memberCount || teamData.membersCount,
        memberCount: teamData.memberCount || teamData.members_count || teamData.membersCount,
        contentCount: teamData.contentCount || teamData.content_count,
        storageUsedBytes: teamData.storageUsedBytes || teamData.storage_used_bytes,
        // Map API v2 field names (createdOn/updatedOn) to our interface (createdAt/updatedAt)
        createdAt: teamData.createdOn || teamData.created_at || teamData.createdAt ?
                  new Date(teamData.createdOn || teamData.created_at || teamData.createdAt) : undefined,
        updatedAt: teamData.updatedOn || teamData.updated_at || teamData.updatedAt ?
                  new Date(teamData.updatedOn || teamData.updated_at || teamData.updatedAt) : undefined
      };
    } catch (error) {
      console.error(`Error fetching team ${teamId}:`, error);
      throw this.errorHandler.handleError(error, `TeamService.getTeam(${teamId})`);
    }
  }

  /**
   * Get team details (alias for backward compatibility)
   */
  async getTeamDetails(teamId: string): Promise<Team> {
    return this.getTeam(teamId);
  }

  /**
   * Get team by ID with organization context
   */
  async getTeamById(teamId: string, organizationId: string): Promise<Team> {
    if (!organizationId) {
      throw new Error('Organization ID is required to fetch team details');
    }
    
    try {
      console.log(`Fetching team ${teamId} with organizationId ${organizationId}`);
      
      const response = await this.getCached<any>(
        `/teams/${teamId}`,
        { organizationId }
      );
      
      // Handle nested team data structure
      const teamData = response.team || response;
      
      return {
        _id: teamData.id || teamData._id,
        id: teamData.id || teamData._id,
        name: teamData.name,
        description: teamData.description,
        organizationId: teamData.organization_id || teamData.organizationId,
        organizationName: teamData.organizationName || teamData.organization_name || 
                         (teamData.organization && teamData.organization.name) || undefined,
        subscriptionId: teamData.subscriptionId || teamData.subscription_id || undefined,
        membersCount: teamData.members_count || teamData.memberCount || teamData.membersCount,
        createdAt: teamData.createdOn || teamData.created_at || teamData.createdAt ? 
                  new Date(teamData.createdOn || teamData.created_at || teamData.createdAt) : undefined,
        updatedAt: teamData.updatedOn || teamData.updated_at || teamData.updatedAt ? 
                  new Date(teamData.updatedOn || teamData.updated_at || teamData.updatedAt) : undefined
      };
    } catch (error) {
      console.error(`Error fetching team ${teamId}:`, error);
      throw this.errorHandler.handleError(error, `TeamService.getTeamById(${teamId})`);
    }
  }

  /**
   * Get team overview with statistics
   */
  async getTeamOverview(teamId: string): Promise<TeamOverview> {
    try {
      console.log(`Fetching team overview: ${teamId}`);
      
      const response = await this.getCached<any>(
        `/teams/${teamId}/overview`
      );
      
      return {
        id: response.id || response._id,
        name: response.name,
        description: response.description || '',
        membersCount: response.membersCount || response.members_count || 0,
        recentActivity: response.recentActivity || response.recent_activity || [],
        statistics: {
          totalContent: response.statistics?.totalContent || response.statistics?.total_content || 0,
          activeMembers: response.statistics?.activeMembers || response.statistics?.active_members || 0,
          storageUsed: response.statistics?.storageUsed || response.statistics?.storage_used || 0
        }
      };
    } catch (error) {
      console.error(`Error fetching team overview ${teamId}:`, error);
      throw this.errorHandler.handleError(error, `TeamService.getTeamOverview(${teamId})`);
    }
  }

  /**
   * Create a new team
   */
  async createTeam(data: CreateTeamRequest): Promise<Team> {
    try {
      console.log('Creating team:', data.name);
      
      const requestBody: any = {
        name: data.name,
        description: data.description
      };
      
      // Add subscription_id if provided
      if (data.subscriptionId) {
        requestBody.subscription_id = data.subscriptionId;
      }
      
      const response = await this.post<any>(
        `/organizations/${data.organizationId}/teams`,
        requestBody
      );

      // Invalidate cache
      this.invalidateCache('organization');

      const teamData = response.team || response;
      
      return {
        _id: teamData.id || teamData._id,
        id: teamData.id || teamData._id,
        name: teamData.name,
        description: teamData.description,
        organizationId: teamData.organization_id || data.organizationId,
        membersCount: teamData.members_count || 1,
        createdAt: teamData.created_at ? new Date(teamData.created_at) : new Date(),
        updatedAt: teamData.updated_at ? new Date(teamData.updated_at) : new Date()
      };
    } catch (error) {
      console.error('Error creating team:', error);
      throw this.errorHandler.handleError(error, 'TeamService.createTeam');
    }
  }

  /**
   * Update team details
   */
  async updateTeam(teamId: string, data: UpdateTeamRequest): Promise<Team> {
    try {
      console.log(`Updating team ${teamId}:`, data);
      
      const response = await this.put<any>(`/teams/${teamId}`, data);
      
      // Invalidate cache
      this.invalidateCache('organization');
      
      return {
        _id: response.id || response._id || '',
        id: response.id || response._id,
        name: response.name || '',
        description: response.description || '',
        organizationId: response.organization_id || '',
        membersCount: response.members_count,
        createdAt: response.created_at ? new Date(response.created_at) : undefined,
        updatedAt: response.updated_at ? new Date(response.updated_at) : undefined
      };
    } catch (error) {
      console.error(`Error updating team ${teamId}:`, error);
      throw this.errorHandler.handleError(error, `TeamService.updateTeam(${teamId})`);
    }
  }

  /**
   * Delete a team
   */
  async deleteTeam(teamId: string): Promise<void> {
    try {
      console.log(`Deleting team: ${teamId}`);

      await this.delete(`/teams/${teamId}`);

      // Invalidate cache
      this.invalidateCache('organization');
    } catch (error) {
      console.error(`Error deleting team ${teamId}:`, error);
      throw this.errorHandler.handleError(error, `TeamService.deleteTeam(${teamId})`);
    }
  }

  /**
   * Leave a team
   */
  async leaveTeam(teamId: string, reason?: string): Promise<void> {
    try {
      console.log(`Leaving team: ${teamId}`);

      await this.post(`/teams/${teamId}/leave`, { reason });

      // Invalidate cache
      this.invalidateCache('organization');
      this.invalidateCache('user');
    } catch (error) {
      console.error(`Error leaving team ${teamId}:`, error);
      throw this.errorHandler.handleError(error, `TeamService.leaveTeam(${teamId})`);
    }
  }

  // ===== TEAM MEMBER OPERATIONS =====

  /**
   * Get team members with pagination
   */
  async getTeamMembers(
    teamId: string,
    page: number = 1,
    limit: number = 20,
    role?: string
  ): Promise<{
    data: TeamMember[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalCount: number;
      pageSize: number;
      hasNext: boolean;
      hasPrevious: boolean;
    };
  }> {
    try {
      console.log(`Fetching members for team ${teamId}`, { page, limit, role });
      
      const params: any = { page, limit };
      if (role) params.role = role;
      
      const response = await this.get<any>(`/teams/${teamId}/members`, {
        params,
        timeout: 10000
      });
      
      // The BaseService.get already extracts the data field from the API response
      // So response here is already the array of members
      const members = Array.isArray(response) ? response : [];
      
      return {
        data: members,
        pagination: {
          currentPage: page,
          totalPages: 1,
          totalCount: members.length,
          pageSize: limit,
          hasNext: false,
          hasPrevious: false
        }
      };
    } catch (error) {
      console.error(`Error fetching members for team ${teamId}:`, error);
      throw this.errorHandler.handleError(error, `TeamService.getTeamMembers(${teamId})`);
    }
  }

  /**
   * Get team members with metadata (for consistent pagination)
   */
  async getTeamMembersWithMetadata(
    teamId: string,
    page: number = 1,
    limit: number = 20,
    role?: string
  ): Promise<ApiResponse<TeamMember[]>> {
    try {
      console.log(`Fetching members with metadata for team ${teamId}`);
      
      const params: any = { page, limit };
      if (role) params.role = role;
      
      const response = await this.requestWithMetadata<TeamMember[]>(
        'GET',
        `/teams/${teamId}/members`,
        undefined,
        { params, timeout: 10000 }
      );
      
      return response;
    } catch (error) {
      console.error(`Error fetching members for team ${teamId}:`, error);
      throw this.errorHandler.handleError(error, `TeamService.getTeamMembersWithMetadata(${teamId})`);
    }
  }

  /**
   * Add a member to team
   */
  async addTeamMember(teamId: string, data: AddTeamMemberRequest): Promise<void> {
    try {
      console.log(`Adding member to team ${teamId}:`, data);
      
      await this.post(`/teams/${teamId}/members`, data);
      
      // Invalidate cache
      this.invalidateCache('organization');
    } catch (error) {
      console.error(`Error adding member to team ${teamId}:`, error);
      throw this.errorHandler.handleError(error, `TeamService.addTeamMember(${teamId})`);
    }
  }

  /**
   * Update team member role
   */
  async updateTeamMemberRole(teamId: string, userId: string, roleId: string): Promise<void> {
    try {
      console.log(`Updating role for member ${userId} in team ${teamId} to role ${roleId}`);

      const requestBody = { roleId: roleId };
      console.log('Request body:', JSON.stringify(requestBody));

      const response = await this.put(`/teams/${teamId}/members/${userId}`, requestBody);
      console.log('Update member role response:', response);

      // Invalidate cache
      this.invalidateCache('organization');
    } catch (error) {
      console.error(`Error updating member role in team ${teamId}:`, error);
      throw this.errorHandler.handleError(error, `TeamService.updateTeamMemberRole(${teamId})`);
    }
  }

  /**
   * Remove a member from team
   */
  async removeTeamMember(teamId: string, userId: string): Promise<void> {
    try {
      console.log(`Removing member ${userId} from team ${teamId}`);
      
      await this.delete(`/teams/${teamId}/members/${userId}`);
      
      // Invalidate cache
      this.invalidateCache('organization');
    } catch (error) {
      console.error(`Error removing member from team ${teamId}:`, error);
      throw this.errorHandler.handleError(error, `TeamService.removeTeamMember(${teamId})`);
    }
  }

  // ===== TEAM ROLE OPERATIONS =====

  /**
   * Get team roles
   */
  async getTeamRoles(teamId: string, includeSystemRoles: boolean = true): Promise<TeamRole[]> {
    try {
      console.log(`Fetching roles for team ${teamId}`);
      
      const params: any = { include_system_roles: includeSystemRoles };
      
      const response = await this.getCached<any>(
        `/teams/${teamId}/roles`,
        params
      );
      
      return response.roles || response || [];
    } catch (error) {
      console.error(`Error fetching roles for team ${teamId}:`, error);
      throw this.errorHandler.handleError(error, `TeamService.getTeamRoles(${teamId})`);
    }
  }

  // ===== TEAM INVITATION OPERATIONS =====

  /**
   * Get team invitations
   */
  async getTeamInvitations(
    teamId: string,
    page: number = 1,
    limit: number = 20,
    status?: string
  ): Promise<{
    data: TeamInvitation[];
    pagination: any;
  }> {
    try {
      console.log(`Fetching invitations for team ${teamId}`);

      // First get the team details to get the organization ID
      const team = await this.getTeamDetails(teamId);
      const organizationId = team.organizationId;

      const params: any = { page, limit, team_id: teamId };
      if (status) params.status = status;

      // Use requestWithMetadata to get both data and metadata
      const response = await this.requestWithMetadata<TeamInvitation[]>(
        'GET',
        `/organizations/${organizationId}/invitations`,
        undefined,
        { params, timeout: 10000 }
      );

      return {
        data: response.data || [],
        pagination: response.metadata?.pagination || {
          currentPage: page,
          totalPages: 1,
          totalCount: 0,
          pageSize: limit,
          hasNext: false,
          hasPrevious: false
        }
      };
    } catch (error) {
      console.error(`Error fetching invitations for team ${teamId}:`, error);
      throw this.errorHandler.handleError(error, `TeamService.getTeamInvitations(${teamId})`);
    }
  }

  /**
   * Create team invitation
   */
  async createTeamInvitation(
    teamId: string,
    data: CreateTeamInvitationRequest
  ): Promise<TeamInvitation> {
    try {
      console.log(`Creating invitation for team ${teamId}`);
      
      // First get the team details to get the organization ID
      const team = await this.getTeamDetails(teamId);
      const organizationId = team.organizationId;
      
      const requestBody = {
        invitee_email: data.invitee_email,
        team_id: teamId,
        role: data.role,
        personal_message: data.personal_message
      };
      
      const response = await this.post<any>(
        `/organizations/${organizationId}/invitations`,
        requestBody
      );
      
      // Invalidate cache
      this.invalidateCache('organization');
      
      const invitationData = response.invitation || response;
      
      return {
        id: invitationData.id || invitationData._id,
        team_id: invitationData.team_id || teamId,
        team_name: invitationData.team_name,
        organization_id: invitationData.organization_id || organizationId,
        organization_name: invitationData.organization_name,
        invitee_email: invitationData.invitee_email,
        inviter_id: invitationData.inviter_id,
        inviter_name: invitationData.inviter_name,
        role_id: invitationData.role_id,
        role_name: invitationData.role_name,
        status: invitationData.status || 'pending',
        personal_message: invitationData.personal_message,
        expires_at: invitationData.expires_at,
        created_at: invitationData.created_at
      };
    } catch (error) {
      console.error(`Error creating invitation for team ${teamId}:`, error);
      throw this.errorHandler.handleError(error, `TeamService.createTeamInvitation(${teamId})`);
    }
  }

  // ===== TEAM SUBSCRIPTION OPERATIONS =====

  /**
   * Get team subscription information
   */
  async getTeamSubscription(teamId: string): Promise<TeamSubscription> {
    try {
      console.log(`Fetching subscription for team ${teamId}`);
      
      const response = await this.getCached<TeamSubscription>(
        `/teams/${teamId}/subscription`
      );
      
      return response;
    } catch (error) {
      console.error(`Error fetching subscription for team ${teamId}:`, error);
      throw this.errorHandler.handleError(error, `TeamService.getTeamSubscription(${teamId})`);
    }
  }

  // ===== TEAM MEMBERSHIP OPERATIONS =====

  /**
   * Get current user's team memberships
   */
  async getTeamMemberships(): Promise<any[]> {
    try {
      console.log('Fetching team memberships');
      
      // Use memberships endpoint to get team memberships
      // Note: There's no direct /users/me/teams endpoint in Gateway V2
      const response = await this.getCached<any[]>('/memberships');
      
      // Convert to membership format
      return (response || []).map((team: any) => ({
        id: team.id || team._id,
        team_id: team.id || team._id,
        team_name: team.name,
        organization_id: team.organization_id || team.organizationId,
        role: team.role || 'member',
        joined_at: team.joined_at || team.created_at
      }));
    } catch (error) {
      console.error('Error fetching team memberships:', error);
      throw this.errorHandler.handleError(error, 'TeamService.getTeamMemberships');
    }
  }
}

// Export singleton instance
export const teamService = new TeamService();